/**
 * BespokeBridge – Donor Management Module API
 *
 * Firestore root-level collections (all scoped by orgId field):
 *   donors/{id}                 — donor organisation profiles
 *   donorContacts/{id}          — key contacts within donor orgs
 *   donorGrants/{id}            — grants with KPI arrays
 *   donorComms/{id}             — communication log entries
 */
import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc,
  query, where, orderBy, serverTimestamp, writeBatch
} from 'firebase/firestore';
import { db } from '../utils/firebase';

// ── Helpers ────────────────────────────────────────────────────────────────
const col  = (name)     => collection(db, name);
const ref  = (name, id) => doc(db, name, id);
const toArr = snap      => snap.docs.map(d => ({ id: d.id, ...d.data() }));
const now  = ()         => serverTimestamp();

export const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const fmtDateTime = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

// ── Constants ──────────────────────────────────────────────────────────────
export const DONOR_TYPES = [
  { value: 'corporate', label: 'Corporate' },
  { value: 'trust',     label: 'Trust / Foundation' },
  { value: 'individual',label: 'Individual' },
  { value: 'government',label: 'Government Body' },
];

export const DONOR_STATUSES = [
  { value: 'prospect', label: 'Prospect',       color: '#6B7280' },
  { value: 'active',   label: 'Active',         color: '#10B981' },
  { value: 'lapsed',   label: 'Lapsed',         color: '#EF4444' },
  { value: 'on-hold',  label: 'On Hold',        color: '#F59E0B' },
];

export const SENIORITY_LEVELS = [
  { value: 'decision-maker', label: 'Decision Maker' },
  { value: 'influencer',     label: 'Influencer' },
  { value: 'coordinator',    label: 'Coordinator' },
  { value: 'staff',          label: 'Staff' },
];

export const GRANT_STATUSES = [
  { value: 'active',    label: 'Active',     color: '#10B981' },
  { value: 'at-risk',   label: 'At Risk',    color: '#F59E0B' },
  { value: 'completed', label: 'Completed',  color: '#6B7280' },
  { value: 'cancelled', label: 'Cancelled',  color: '#EF4444' },
];

export const COMM_TYPES = [
  { value: 'email',   label: 'Email' },
  { value: 'call',    label: 'Phone Call' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'note',    label: 'Note' },
];

export const COMM_DIRECTIONS = [
  { value: 'outbound', label: 'Outbound' },
  { value: 'inbound',  label: 'Inbound' },
];

// ── KPI helpers ─────────────────────────────────────────────────────────────
export function calcKpiProgress(kpi) {
  if (!kpi || !kpi.target) return 0;
  return Math.min(100, Math.round((kpi.current / kpi.target) * 100));
}

export function kpiRiskLevel(kpi) {
  const pct = calcKpiProgress(kpi);
  if (pct >= 80) return 'on-track';
  if (pct >= 50) return 'at-risk';
  return 'critical';
}

// ── Days until date helper ──────────────────────────────────────────────────
export function daysUntil(ts) {
  if (!ts) return null;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return Math.ceil((d - Date.now()) / 86400000);
}

// ═══════════════════════════════════════════════════════════════════════════
// DONORS
// ═══════════════════════════════════════════════════════════════════════════
export async function getDonors(orgId) {
  const q = query(col('donors'), where('orgId', '==', orgId), orderBy('name', 'asc'));
  return toArr(await getDocs(q));
}

export async function getDonor(donorId) {
  const snap = await getDoc(ref('donors', donorId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function createDonor(orgId, data) {
  return addDoc(col('donors'), { ...data, orgId, createdAt: now(), updatedAt: now() });
}

export async function updateDonor(donorId, data) {
  return updateDoc(ref('donors', donorId), { ...data, updatedAt: now() });
}

export async function deleteDonor(donorId) {
  return deleteDoc(ref('donors', donorId));
}

// ═══════════════════════════════════════════════════════════════════════════
// DONOR CONTACTS
// ═══════════════════════════════════════════════════════════════════════════
export async function getDonorContacts(orgId, donorId = null) {
  const constraints = [where('orgId', '==', orgId)];
  if (donorId) constraints.push(where('donorId', '==', donorId));
  constraints.push(orderBy('name', 'asc'));
  return toArr(await getDocs(query(col('donorContacts'), ...constraints)));
}

export async function createDonorContact(orgId, data) {
  return addDoc(col('donorContacts'), {
    ...data, orgId,
    lastContactedAt: null,
    createdAt: now(),
  });
}

export async function updateDonorContact(contactId, data) {
  return updateDoc(ref('donorContacts', contactId), { ...data, updatedAt: now() });
}

export async function deleteDonorContact(contactId) {
  return deleteDoc(ref('donorContacts', contactId));
}

// ═══════════════════════════════════════════════════════════════════════════
// DONOR GRANTS & KPIS
// ═══════════════════════════════════════════════════════════════════════════
export async function getDonorGrants(orgId, donorId = null) {
  const constraints = [where('orgId', '==', orgId)];
  if (donorId) constraints.push(where('donorId', '==', donorId));
  constraints.push(orderBy('createdAt', 'desc'));
  return toArr(await getDocs(query(col('donorGrants'), ...constraints)));
}

export async function createDonorGrant(orgId, data) {
  return addDoc(col('donorGrants'), {
    ...data,
    orgId,
    kpis: data.kpis || [],
    status: data.status || 'active',
    createdAt: now(),
    updatedAt: now(),
  });
}

export async function updateDonorGrant(grantId, data) {
  return updateDoc(ref('donorGrants', grantId), { ...data, updatedAt: now() });
}

export async function deleteDonorGrant(grantId) {
  return deleteDoc(ref('donorGrants', grantId));
}

export async function updateKpi(grantId, kpiIndex, kpiData, allKpis) {
  const updated = [...allKpis];
  updated[kpiIndex] = { ...updated[kpiIndex], ...kpiData };
  return updateDoc(ref('donorGrants', grantId), { kpis: updated, updatedAt: now() });
}

// ═══════════════════════════════════════════════════════════════════════════
// DONOR COMMS LOG
// ═══════════════════════════════════════════════════════════════════════════
export async function getDonorComms(orgId, donorId = null) {
  const constraints = [where('orgId', '==', orgId)];
  if (donorId) constraints.push(where('donorId', '==', donorId));
  constraints.push(orderBy('createdAt', 'desc'));
  return toArr(await getDocs(query(col('donorComms'), ...constraints)));
}

export async function createDonorComm(orgId, data) {
  return addDoc(col('donorComms'), { ...data, orgId, createdAt: now() });
}

export async function updateDonorComm(commId, data) {
  return updateDoc(ref('donorComms', commId), { ...data, updatedAt: now() });
}

export async function deleteDonorComm(commId) {
  return deleteDoc(ref('donorComms', commId));
}
